/**
 * External dependencies
 */
import { useQuery } from '@tanstack/react-query';

/**
 * Internal dependencies
 */

const formatAvailableCountries = (countries) => {
	if (!countries) {
		return '';
	}

	return countries.toString();
};

/**
 * Fetch instances
 * @returns {object}
 */
function useLocalEvents({ filter, group = false, perPage = 10, limit = -1 }) {
	const { isPending, isError, data, error, refetch } = useQuery({
		queryKey: ['filter'],
		queryFn: async () => {
			const baseUrl = `${window.visitEduSettings.apiBaseUrl}/wp-json/visitedu-block-theme/v1/events`;

			// Build url with query parameters
			const url = new URL(baseUrl);

			// Add query parameters
			url.searchParams.append('per_page', perPage);

			if (limit > 0) {
				url.searchParams.append('limit', limit);
			}

			url.searchParams.append(
				'available_countries',
				formatAvailableCountries(
					window.visitEduSettings.availableCountries
				)
			);

			if (filter?.categories?.length > 0) {
				url.searchParams.append(
					'category',
					filter.categories.toString()
				);
			}

			if (filter?.statuses?.length > 0) {
				url.searchParams.append('status', filter.statuses.toString());
			}

			if (filter?.countries?.length > 0) {
				url.searchParams.append('country', filter.countries.toString());
			}

			if (filter?.venues?.length > 0) {
				url.searchParams.append('venue', filter.venues.toString());
			}

			if (filter?.name) {
				url.searchParams.append('name', filter.name.toString());
			}

			const response = await fetch(url.href);

			const json = await response.json();

			/**
			 * If group is true, group instances by month and year
			 */
			if (group) {
				const options = { month: 'long', year: 'numeric' };
				const formatter = new Intl.DateTimeFormat('en-US', options);
				const data = json.data;

				const courseInstances = data.reduce((acc, instance) => {
					/**
					 * Handle cases where start_date is null
					 * This can happen when the event is e.g. on-demand
					 */
					if (instance.start_date === null) {
						const key = 'On Demand';
						if (!acc[key]) {
							acc[key] = [];
						}

						acc[key].push(instance);
						return acc;
					}

					/**
					 * Handle normal cases where instance has a start_date
					 * Group instances by month and year
					 */
					const date = new Date(instance.start_date);

					const key = `${formatter.format(date)}`;
					if (!acc[key]) {
						acc[key] = [];
					}
					acc[key].push(instance);
					return acc;
				}, {});

				// Move on-demand instances to first position
				const onDemandInstances = courseInstances['On Demand'];
				delete courseInstances['On Demand'];

				const sortedCourseInstances = {
					'On Demand': onDemandInstances,
					...courseInstances,
				};

				return sortedCourseInstances;
			}

			/**
			 * If group is false, return all instances without grouping
			 */
			return json.data;
		},
	});

	return {
		data,
		error,
		isError,
		isPending,
		refetch,
	};
}

export { useLocalEvents };
